import React, { useEffect, useState, useRef, useContext } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../..";
import "./MainDashboardCard.css";
import { Eye } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { getData } from "../../../Services/Services";
import {
  coldwavechartdata,
  coldwavecardsdata,
  avalanchechartdata,
  avalanchecardsdata,
  cloudburstchartdata,
  cloudburstcardsdata,
  getThemeURL
} from "../../../Services/EndPoints";
import moment from "moment";
import {
  ColdWaveDashboard,
  FireDashboard,
  FloodDashboardIcon,
  HeatWaveDashboard,
} from "../../../Components/IconComponents";

export default function MainDashboardCard({
  data,
  config,
  index = 0,
  fromDate,
  toDate,
  stateId,
  refreshSignal = 0,
}) {
  const colors = document.getElementById("light")
    ? ["#0A2738", "#082A49", "#174066", "#2E5C85", "#4A7EA5"]
    : ["#519EFE", "#b981f6", "#E28771", "#D252AA"];

  const [cloudBurstCardData, setCloudBurstCardData] = useState({});
  const [coldcardData, setColdCardData] = useState({});
  const [avalancheCardData, setAvalancheCardData] = useState({});
  const [chartData, setChartData] = useState([]);
  const { theme } = useContext(ThemeContext);
  const echartsDom = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chart = useRef(null);

  const colorMap = {
    "Heat Wave": "#2E5C85",
    "Cold Wave": "#174066",
    Fire: "#082A49",
  };

  // Display name mapping for titles
  const displayNameMap =
    stateId === 62
      ? {
        "Cold Wave": "Glof",
        Fire: "Avalanche",
        "Heat Wave": "Cloud Burst",
        Floods: "Floods",
      }
      : {
        "Cold Wave": "Cold Wave",
        Fire: "Fire",
        "Heat Wave": "Heat Wave",
        Floods: "Floods",
      };

  console.log(coldcardData, "colddata");

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        // Was hardcoded to "2025-01-01" → today regardless of the fromDate/
        // toDate props, so this chart never actually reflected whatever
        // date range the dashboard's picker was set to — use the real
        // props instead, same as fetchCloudBurstCardData/fetchColdWaveCardData/
        // fetchAvalancheCardData below already do.
        const startDateStr = moment(fromDate).format("YYYY-MM-DD");
        const endDateStr = moment(toDate).format("YYYY-MM-DD");
        let apiUrl = "";
        let payloadType = "no_of_deaths_today";

        if (config.title === "Heat Wave") {
          apiUrl = cloudburstchartdata;
        } else if (config.title === "Cold Wave") {
          apiUrl = coldwavechartdata;
          payloadType = "number_of_deaths";
        } else if (config.title === "Fire") {
          apiUrl = avalanchechartdata;
          payloadType = "no_of_deaths_today";
        }

        if (apiUrl) {
          const response = await getData(
            apiUrl
              .replace("FROMDATE", startDateStr)
              .replace("TODATE", endDateStr)
              .replace("BARTYPE", payloadType)
          );
          setChartData(response || []);
        }
      } catch (error) {
        console.error(`Error fetching ${config.title} data:`, error);
      }
    };

    fetchChartData();
  }, [config.title, fromDate, toDate, refreshSignal]);

  useEffect(() => {
    if (echartsDom.current) {
      if (chart.current) {
        chart.current.dispose();
      }
      chart.current = echarts.init(echartsDom.current);

      const handleResize = () => {
        if (chart.current) {
          chart.current.resize();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (chart.current) {
          chart.current.dispose();
        }
      };
    }
  }, []);

  // Donut Chart for Cold Wave Dashboard (Glof)
  const renderDonutChart = () => {
    if (chartData.length > 0) {
      const filteredData = chartData.filter((item) => item.count > 0);
      const sortedData = [...filteredData]
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      const colorList = [
        "#0a3d6b", "#0f4c81", "#145c97", "#1a6cad",
        "#1f7cc3", "#258cd9", "#3a9be0", "#5cade6",
      ];
      const total = sortedData.reduce((s, d) => s + d.count, 0);

      const options = {
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            return `<div style="font-weight:600;margin-bottom:2px">${params.name}</div>
                    <div style="font-size:13px">${params.value} affected (${params.percent}%)</div>`;
          },
          confine: true,
          backgroundColor: theme === "dark" ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.95)",
          borderColor: theme === "dark" ? "#444" : "#e5e7eb",
          borderRadius: 8, padding: [8, 12],
          textStyle: { color: theme === "dark" ? "#fff" : "#333", fontSize: 11 },
          extraCssText: "box-shadow: 0 4px 14px rgba(0,0,0,0.12);",
        },
        graphic: [
          {
            type: "text",
            left: "center",
            top: "42%",
            style: {
              text: total.toString(),
              textAlign: "center",
              fill: theme === "dark" ? "#fff" : "#1f2937",
              fontSize: 16,
              fontWeight: 800,
            },
          },
          {
            type: "text",
            left: "center",
            top: "52%",
            style: {
              text: "Total",
              textAlign: "center",
              fill: theme === "dark" ? "#aaa" : "#9ca3af",
              fontSize: 9,
            },
          },
        ],
        series: [
          {
            type: "pie",
            radius: ["45%", "72%"],
            center: ["50%", "50%"],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 5,
              borderColor: theme === "dark" ? "#1a1a2e" : "#fff",
              borderWidth: 2,
            },
            label: {
              show: true,
              fontSize: 8,
              color: theme === "dark" ? "#ccc" : "#4b5563",
              formatter: (p) => p.name.length > 8 ? p.name.substring(0, 7) + ".." : p.name,
              overflow: "truncate", width: 55,
            },
            labelLine: {
              show: true, length: 6, length2: 8,
              lineStyle: { color: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" },
            },
            emphasis: {
              scaleSize: 6,
              itemStyle: { shadowBlur: 12, shadowColor: "rgba(0,0,0,0.25)" },
            },
            data: sortedData.map((item, idx) => ({
              value: item.count,
              name: item.dist_name,
              itemStyle: { color: colorList[idx % colorList.length] },
            })),
          },
        ],
      };

      chart.current.setOption(options);
    } else {
      chart.current.setOption({
        title: {
          text: "No Data Available", left: "center", top: "middle",
          textStyle: { color: theme === "dark" ? "#fff" : "#333", fontSize: 16 }
        },
      });
    }
  };

  // Nightingale rose (polar) chart for Fire Dashboard (Avalanche) — a
  // district-wise human-loss breakdown that stays visually distinct from
  // the Cloud Burst vertical bar chart. Same source (`chartData`), same
  // "No Data Available" fallback.
  const renderGaugeChart = () => {
    if (chartData.length > 0) {
      const filteredData = chartData.filter((item) => item.count > 0);
      const sortedData = [...filteredData]
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      const colorList = ["#082A49", "#0e447e", "#155fb3", "#1c7be8", "#3a9be0", "#7ec0ec", "#a7d3f0"];
      const total = sortedData.reduce((s, d) => s + d.count, 0);

      const options = {
        tooltip: {
          trigger: "item",
          formatter: (params) =>
            `<div style="font-weight:600;margin-bottom:2px">${params.name}</div>
             <div style="font-size:13px">${params.value} human loss</div>`,
          confine: true,
          backgroundColor: theme === "dark" ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.95)",
          borderColor: theme === "dark" ? "#444" : "#e5e7eb",
          borderRadius: 8, padding: [8, 12],
          textStyle: { color: theme === "dark" ? "#fff" : "#333", fontSize: 11 },
          extraCssText: "box-shadow: 0 4px 14px rgba(0,0,0,0.12);",
        },
        graphic: [
          {
            type: "text", left: "center", top: "44%",
            style: {
              text: total.toString(), textAlign: "center",
              fill: theme === "dark" ? "#fff" : "#1f2937", fontSize: 18, fontWeight: 800,
            },
          },
          {
            type: "text", left: "center", top: "54%",
            style: {
              text: "Human Loss", textAlign: "center",
              fill: theme === "dark" ? "#aaa" : "#9ca3af", fontSize: 9,
            },
          },
        ],
        series: [
          {
            type: "pie",
            radius: ["35%", "72%"],
            center: ["50%", "50%"],
            roseType: "area",
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 6,
              borderColor: theme === "dark" ? "#1a1a2e" : "#fff",
              borderWidth: 2,
            },
            label: {
              show: true,
              fontSize: 9,
              color: theme === "dark" ? "#ccc" : "#4b5563",
              formatter: (p) => `${p.name.length > 8 ? p.name.substring(0, 7) + ".." : p.name}: ${p.value}`,
            },
            labelLine: {
              show: true, length: 6, length2: 8,
              lineStyle: { color: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" },
            },
            emphasis: {
              scaleSize: 6,
              itemStyle: { shadowBlur: 12, shadowColor: "rgba(0,0,0,0.25)" },
            },
            data: sortedData.map((item, idx) => ({
              value: item.count,
              name: item.dist_name,
              itemStyle: { color: colorList[idx % colorList.length] },
            })),
          },
        ],
        animationDuration: 900,
        animationEasing: "cubicOut",
      };

      chart.current.setOption(options);
    } else {
      chart.current.setOption({
        title: {
          text: "No Data Available", left: "center", top: "middle",
          textStyle: { color: theme === "dark" ? "#fff" : "#333", fontSize: 10 }
        },
      });
    }
  };

  useEffect(() => {
    if (chart.current) {
      chart.current.clear();

      if (chartData.length > 0) {
        const filteredData = chartData.filter((item) => item.count > 0);
        const sortedData = [...filteredData].sort((a, b) => a.count - b.count);

        const colorList = [
          "#174066", "#1d5285", "#2364a3",
          "#2975c2", "#3086e0", "#3e95ee", "#509efa",
        ];

        if (config.title === "Heat Wave") {
          // Cloud Burst — district-wise vertical gradient bar chart.
          // Replaces the earlier pulsing bubble scatter, which read as
          // just a couple of floating dots when few districts reported.
          const barData = [...sortedData].sort((a, b) => b.count - a.count).slice(0, 8);
          const options = {
            tooltip: {
              trigger: "axis",
              axisPointer: { type: "shadow" },
              formatter: (params) => {
                const p = params[0];
                return `<div style="font-weight:600;margin-bottom:2px">${p.name}</div>
                        <div style="font-size:13px">${p.value} human loss</div>`;
              },
              confine: true,
              backgroundColor: theme === "dark" ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.95)",
              borderColor: theme === "dark" ? "#444" : "#e5e7eb",
              borderRadius: 8, padding: [8, 12],
              textStyle: { color: theme === "dark" ? "#fff" : "#333", fontSize: 11 },
              extraCssText: "box-shadow: 0 4px 14px rgba(0,0,0,0.12);",
            },
            grid: { left: "5%", right: "5%", top: "14%", bottom: "3%", containLabel: true },
            xAxis: {
              type: "category",
              data: barData.map(d => d.dist_name.length > 8 ? d.dist_name.substring(0, 7) + ".." : d.dist_name),
              axisLabel: {
                color: theme === "dark" ? "#aaa" : "#6b7280",
                fontSize: 9,
                rotate: barData.length > 5 ? 30 : 0,
              },
              axisLine: { lineStyle: { color: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" } },
              axisTick: { show: false },
            },
            yAxis: {
              type: "value",
              axisLabel: { color: theme === "dark" ? "#aaa" : "#9ca3af", fontSize: 9 },
              splitLine: { lineStyle: { color: theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", type: "dashed" } },
              axisLine: { show: false },
              axisTick: { show: false },
            },
            series: [
              {
                type: "bar",
                barWidth: "45%",
                data: barData.map((item, idx) => ({
                  value: item.count,
                  itemStyle: {
                    borderRadius: [5, 5, 0, 0],
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 0, color: colorList[idx % colorList.length] },
                      { offset: 1, color: colorList[Math.min(idx + 2, colorList.length - 1)] },
                    ]),
                    shadowBlur: 6,
                    shadowColor: "rgba(0,60,140,0.15)",
                  },
                })),
                label: {
                  show: true, position: "top", fontSize: 9, fontWeight: 700,
                  color: theme === "dark" ? "#ccc" : "#374151",
                },
                emphasis: {
                  itemStyle: { shadowBlur: 12, shadowColor: "rgba(0,60,140,0.3)" },
                },
              },
            ],
            animationDuration: 900,
            animationEasing: "cubicOut",
          };

          chart.current.setOption(options);
        } else if (config.title === "Cold Wave") {
          // Glof — Donut Chart
          renderDonutChart();
        } else if (config.title === "Fire") {
          // Avalanche — Nightingale rose (polar) chart
          renderGaugeChart();
        }
      } else {
        chart.current.setOption({
          title: {
            text: "No Data Available",
            left: "center",
            top: "middle",
            textStyle: {
              color: theme === "dark" ? "#fff" : "#333",
              fontSize: 16,
            },
          },
        });
      }
    }
  }, [chartData, theme, config.title, fromDate, toDate]);

  // Floods — Premium Area Chart using card data
  useEffect(() => {
    if (config.title !== "Floods" || !chart.current || !data) return;
    chart.current.clear();

    const floodItems = (config.data || []).map((conf) => ({
      label: conf.label,
      value: data?.[conf.key] || 0,
    }));

    // Find min/max for dynamic Y axis to make curves more pronounced
    const maxValue = Math.max(...floodItems.map(d => d.value));

    const options = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: { backgroundColor: "#1e3a8a", color: "#fff" }
        },
        formatter: (params) => {
          const p = params[0];
          return `<div style="font-weight:600;margin-bottom:4px;color:${theme ==='dark' ? '#fff' : '#1f2937'}">${p.name}</div>
                  <div style="font-size:14px;color:#2563eb;font-weight:700">
                    <span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:#2563eb;"></span>
                    ${p.value.toLocaleString()}
                  </div>`;
        },
        backgroundColor: theme === "dark" ? "rgba(15, 23, 42, 0.9)" : "rgba(255,255,255,0.95)",
        borderColor: theme === "dark" ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        padding: [12, 16],
        textStyle: { fontFamily: "Inter, sans-serif" },
        extraCssText: "box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1); border-radius: 8px;",
      },
      grid: {
        left: "3%",
        right: "6%",
        top: "16%",
        bottom: "10%",
        containLabel: true
      },
      xAxis: [
        {
          type: "category",
          boundaryGap: true,
          data: floodItems.map((d) => d.label.split(" ").join("\n")),
          axisLabel: {
            color: theme === "dark" ? "#94a3b8" : "#64748b",
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 14,
            margin: 16
          },
          axisLine: {
            lineStyle: { color: theme === "dark" ? "#334155" : "#e2e8f0" }
          },
          axisTick: { show: false },
        }
      ],
      yAxis: [
        {
          type: "value",
          max: maxValue > 0 ? maxValue * 1.2 : null, // Add 20% headroom for labels
          axisLabel: {
            color: theme === "dark" ? "#94a3b8" : "#64748b",
            fontSize: 10,
            formatter: (value) => {
              if (value >= 1000) return (value / 1000).toFixed(1) + "k";
              return value;
            }
          },
          splitLine: {
            lineStyle: {
              color: theme === "dark" ? "#334155" : "#f1f5f9",
              type: "solid"
            }
          },
          axisLine: { show: false },
          axisTick: { show: false },
        }
      ],
      series: [
        {
          name: "Floods Impact",
          type: "line",
          smooth: 0.4, // Curvy line
          symbol: "circle",
          symbolSize: 8,
          showSymbol: true,
          itemStyle: {
            color: "#3b82f6", // Bright blue points
            borderColor: "#fff",
            borderWidth: 2,
            shadowColor: "rgba(59, 130, 246, 0.5)",
            shadowBlur: 8,
          },
          lineStyle: {
            width: 3,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: "#2563eb" },
              { offset: 1, color: "#60a5fa" }
            ]),
            shadowColor: "rgba(37, 99, 235, 0.2)",
            shadowBlur: 10,
            shadowOffsetY: 5
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(59, 130, 246, 0.4)" }, // Top opacity
              { offset: 0.8, color: "rgba(59, 130, 246, 0.05)" },
              { offset: 1, color: "rgba(59, 130, 246, 0)" } // Bottom transparent fading
            ])
          },
          label: {
            show: true,
            position: "top",
            distance: 10,
            formatter: "{c}",
            color: theme === "dark" ? "#fff" : "#1e293b",
            fontSize: 12,
            fontWeight: 700,
            backgroundColor: theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.8)",
            padding: [4, 8],
            borderRadius: 4,
            shadowColor: "rgba(0,0,0,0.1)",
            shadowBlur: 4,
            shadowOffsetY: 2
          },
          data: floodItems.map((item) => item.value)
        }
      ],
      animationDuration: 1500,
      animationEasing: "cubicInOut"
    };

    chart.current.setOption(options);
  }, [data, config, theme]);

  const navigateToReport = (title) => {
    const reportRoutes =
      stateId === 62
        ? {
          "Heat Wave": "/dmsdashboard/cloudbrust-dashboard",
          "Cold Wave": "/dmsdashboard/Glof-dashboard",
          Fire: "/dmsdashboard/Avalanche-dashboard",
          Floods: "/dmsdashboard/dist-dashboard",
        }
        : {
          "Heat Wave": "/dmsdashboard/heatwave-dashboard",
          "Cold Wave": "/dmsdashboard/coldwave-dashboard",
          Fire: "/dmsdashboard/fire-dashboard",
          Floods: "/dmsdashboard/dist-dashboard",
        };
    navigate(reportRoutes[title]);
  };

  useEffect(() => {
    fetchCloudBurstCardData();
    fetchColdWaveCardData();
    fetchAvalancheCardData();
  }, [fromDate, toDate, refreshSignal]);

  console.log(cloudBurstCardData, "hhh");

  const fetchCloudBurstCardData = async () => {
    try {
      const startDateStr = moment(fromDate).format("YYYY-MM-DD");
      const endDateStr = moment(toDate).format("YYYY-MM-DD");
      const response = await getData(
        cloudburstcardsdata
          .replace("FROMDATE", startDateStr)
          .replace("TODATE", endDateStr)
      );
      setCloudBurstCardData(response[0] || {});
    } catch (error) {
      console.error("Error fetching cloud burst card data:", error);
    }
  };
  const fetchColdWaveCardData = async () => {
    try {
      const startDateStr = moment(fromDate).format("YYYY-MM-DD");
      const endDateStr = moment(toDate).format("YYYY-MM-DD");
      const response = await getData(
        coldwavecardsdata
          .replace("FROMDATE", startDateStr)
          .replace("TODATE", endDateStr)
      );
      setColdCardData(response[0] || {});
    } catch (error) {
      console.error("Error fetching cold wave card data:", error);
    }
  };
  const fetchAvalancheCardData = async () => {
    try {
      const startDateStr = moment(fromDate).format("YYYY-MM-DD");
      const endDateStr = moment(toDate).format("YYYY-MM-DD");
      const response = await getData(
        avalanchecardsdata
          .replace("FROMDATE", startDateStr)
          .replace("TODATE", endDateStr)
      );
      setAvalancheCardData(response[0] || {});
    } catch (error) {
      console.error("Error fetching avalanche card data:", error);
    }
  };

  console.log(avalancheCardData, "vvv");

  return (
    <div
      className={`dashboard_card_1 ${config.title
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
    >
      <div
        className="card-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ marginTop: "-5px" }}>{config.icon}</span>
          <span>{displayNameMap[config.title] || config.title}</span>
        </div>

        <Eye
          className="eye-icon"
          size={20}
          title="View Detailed Report"
          onClick={() => navigateToReport(config.title)}
          style={{ cursor: "pointer" }}
        />
      </div>
      <span>
        {`${config.title1}: ${{
          Floods: data?.humans_loss || 0,
          "Heat Wave": cloudBurstCardData?.no_of_deaths_today || 0,
          "Cold Wave": coldcardData?.number_of_deaths || 0,
          Fire: avalancheCardData?.no_of_deaths_today || 0,
        }[config.title] ?? 0
          }`}
      </span>

      {config.title !== "Floods" && (
        <div className="d-grid gap-2 mt-1">
          {config?.data.map((conf, idx) => (
            <div
              key={idx}
              className="ds-single_card-2"
              style={{ backgroundColor: colors[idx % colors.length] }}
            >
              <div className="card-label-main">{conf.label}</div>
              <div className="card-data-main">
                {data && data[conf?.key]
                  ? data[conf?.key]
                  : 0}
              </div>
            </div>
          ))}
        </div>
      )}
      <div ref={echartsDom} style={{ height: config.title === "Floods" ? "280px" : "300px", marginTop: "2px" }} />
    </div>
  );
}